import { action, computed, observable, runInAction } from 'mobx';
import React from 'react';
import { GridColDef } from '../../components/Grid/Grid';
import AbstractStore from '../../store/AbstractStore';
import { USERS_PATH } from '../../store/constants';
import type { Role, User } from '../../types/practice-types';
import { parseCsvFile } from '../../utils/csv-utils';
import {
  AddUsersActionsRenderer,
  addUsersRowCellRenderer,
  ModalCustomFooter,
} from './addUsersRenderers';

interface Loaders {
  addUsers?: boolean;
  validateUsers?: boolean;
  createUsers?: boolean;
}

interface UserEditObject {
  user: User;
  errors: Partial<Record<keyof User, string>>;
  roleIndices: number[];
}

const emptyUser = (): User => ({
  firstName: '',
  lastName: '',
  email: '@idexx.com',
  phone: '',
  roles: [],
});

const USER_FIELDS: (keyof User)[] = ['firstName', 'lastName', 'email', 'phone', 'roles'];

export default class AddUsersStore extends AbstractStore {
  @observable accessor loaders: Loaders = {};

  @observable accessor userCreateObjects: UserEditObject[] = [];

  @observable accessor validated = false;

  @computed
  public get roles(): Role[] {
    return this.root.usersManagementStore.globalRoles;
  }

  @computed
  public get usersValid(): boolean {
    if (!this.validated || this.userCreateObjects.length === 0) return false;
    for (const userEditObject of this.userCreateObjects) {
      if (Object.keys(userEditObject.errors).length > 0) return false;
    }
    return true;
  }

  public get addUsersColDef(): GridColDef<User>[] {
    return [
      {
        headerName: 'First Name',
        field: 'firstName',
        renderer: addUsersRowCellRenderer('firstName'),
      },
      { headerName: 'Last Name', field: 'lastName', renderer: addUsersRowCellRenderer('lastName') },
      { headerName: 'Email', field: 'email', renderer: addUsersRowCellRenderer('email') },
      { headerName: 'Phone (mobile)', field: 'phone', renderer: addUsersRowCellRenderer('phone') },
      { headerName: 'Roles', field: 'roles', renderer: addUsersRowCellRenderer('roles') },
      { headerName: 'Actions', field: 'actions', renderer: AddUsersActionsRenderer },
    ];
  }

  @action
  public init(): void {
    this.root.globalModalStore.setModalContent({
      customFooter: React.createElement(ModalCustomFooter),
    });
    this.root.globalModalStore.globalModalState.wide = true;
    this.loaders = {};
    this.userCreateObjects = [];
    this.addUsersRow();
    this.validated = false;
  }

  @action
  public async processUsersFile(file: File): Promise<void> {
    runInAction(() => (this.loaders.addUsers = true));
    const userCreateObjects: UserEditObject[] = [];
    try {
      const users = await parseCsvFile<User>(file, USER_FIELDS);
      users.forEach(user => {
        Object.keys(user).forEach(key => {
          const fieldName = key as keyof User;
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          user[fieldName] = String(user[fieldName] || '').trim();
        });
        const roleDescriptions = ((user.roles as unknown as string) || '')
          .split(';')
          .map(r => r.trim());
        const userEditObject: UserEditObject = { user, errors: {}, roleIndices: [] };
        const roles: Role[] = [];
        roleDescriptions.forEach(roleDescription => {
          const index = this.roles.findIndex(role => role.description === roleDescription);
          if (index > -1) {
            userEditObject.roleIndices.push(index);
            roles.push(this.roles[index]);
          }
        });
        user.roles = roles;
        userCreateObjects.push(userEditObject);
      });
      runInAction(() => {
        this.userCreateObjects = userCreateObjects;
        this.validated = false;
      });
    } catch {
      alert(`Error parsing CSV file`);
    }
    runInAction(() => (this.loaders.addUsers = false));
  }

  @action
  public addUsersRow(): void {
    this.userCreateObjects.push({ user: emptyUser(), errors: {}, roleIndices: [] });
    this.validated = false;
  }

  @action
  public deleteAddUsersRow(row: any): void {
    const index = this.userCreateObjects.map(o => o.user).findIndex(r => r === row);
    this.userCreateObjects.splice(index, 1);
  }

  @action
  public changeRoleIndices(userIndex: number, roleIndices: number[]): void {
    this.userCreateObjects[userIndex].roleIndices = roleIndices;
    this.userCreateObjects[userIndex].user.roles = roleIndices.map(i => this.roles[i]);
    this.validated = false;
  }

  @action
  public async validateAddUsers(): Promise<void> {
    runInAction(() => (this.loaders.validateUsers = true));
    try {
      const errors = await this.root.apiStore.backendPost<Partial<Record<keyof User, string>>>(
        USERS_PATH,
        { action: 'VALIDATE_MULTIPLE', users: this.toBackendUsersPayload() },
      );
      runInAction(() => {
        this.userCreateObjects.forEach((obj, index) => {
          obj.errors = errors[index];
        });
        this.validated = true;
      });
    } catch (e: any) {
      alert(`Validation error: ${e.message}`);
    }
    runInAction(() => {
      this.loaders.validateUsers = false;
    });
  }

  @action
  public async createUsers(): Promise<void> {
    runInAction(() => {
      this.loaders.createUsers = true;
      this.root.globalModalStore.globalModalState.okDisabled = true;
    });
    try {
      await this.root.apiStore.backendPost(USERS_PATH, {
        action: 'CREATE_MULTIPLE',
        users: this.toBackendUsersPayload(),
      });
      runInAction(() => {
        this.loaders.createUsers = false;
        this.root.globalModalStore.closeGlobalModal();
        void this.root.usersManagementStore.fetchUsers();
      });
    } catch (e: any) {
      alert(`User creation error: ${e.message}`);
    }
    runInAction(() => {
      this.loaders.createUsers = false;
    });
  }

  private toBackendUsersPayload(): any {
    return this.userCreateObjects.map(obj => ({
      ...obj.user,
      roleNames: obj.user.roles.map(r => r.name),
      roles: undefined,
    }));
  }
}
